import { useMemo, type FC, useEffect } from 'react'
import { useRouter } from 'next/router'
import Head from 'next/head'
import clsx from 'clsx'

import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'

import { IS_DEVELOPMENT } from 'app/KinerjaShared/constants/env'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'
import { useMediaQueryHelper } from 'app/PemetaanKompetensiPage/hooks/useMediaQueryHelper'
import {
  PENGELOLAAN_KINERJA_GURU_PATH,
  PENGELOLAAN_KINERJA_PAMONG_PATH,
  PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH,
} from 'app/KinerjaShared/constants/path'
import ResetButtons from 'app/KinerjaPage/components/ResetButtons'
import { PageHeader } from 'app/KinerjaShared/components/PageHeader'
import { InvalidUserNoAccess } from 'api/kinerja/kepala-sekolah/config'
import useToggleSdkContext from 'utils/useToogleSdk'

import type { KinerjaLayoutBase as TKinerjaLayoutBase } from 'types/kinerja-layout'
import type { KinerjaTipe } from 'types/kinerja'

const RenderSpinner = () => {
  return (
    <div className="flex flex-col gap-4 justify-center items-center h-screen">
      <Spinner data-testid="loading-spinner" />
      {IS_DEVELOPMENT && <Text>Loading user profile...</Text>}
    </div>
  )
}

const RenderResetButtons = () => {
  const toggles = useToggleSdkContext()
  const isKinerjaWithTipeEnabled =
    toggles?.['kinerja-with-tipe-enabled'] === true

  const router = useRouter()

  if (
    router.asPath.includes(PENGELOLAAN_KINERJA_GURU_PATH) ||
    router.asPath.includes(PENGELOLAAN_KINERJA_PAMONG_PATH) ||
    router.asPath.includes(PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH)
  ) {
    let tipe: KinerjaTipe

    if (isKinerjaWithTipeEnabled) {
      if (router.asPath.includes(PENGELOLAAN_KINERJA_GURU_PATH)) {
        tipe = 'SPF'
      } else if (router.asPath.includes(PENGELOLAAN_KINERJA_PAMONG_PATH)) {
        tipe = 'SPNF'
      } else {
        tipe = 'GPK'
      }
    }

    return <ResetButtons tipe={tipe} />
  }

  return null
}

const KinerjaLayoutBase: FC<TKinerjaLayoutBase> = ({
  pageTitle,
  title,
  children,
  hasHeader = true,
  centered = false,
  printablePage = false,
}) => {
  const { isDesktop } = useMediaQueryHelper()
  const { isLoading, isError, isGuru, isPamong, isGpkPegawai } =
    useRoleContext()

  const isDesktopBrowser = useMemo(() => {
    return isDesktop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isLoading && isError) throw new InvalidUserNoAccess('', null)
  }, [isError, isLoading])

  return (
    <>
      <Head>
        <title>
          {pageTitle ? `${pageTitle} - ` : ''}
          {title}
        </title>

        {!isDesktopBrowser && (
          <meta
            name="viewport"
            content="width=1440, initial-scale=0.1, maximum-scale=1.0, user-scalable=yes"
          />
        )}
      </Head>

      <div
        data-module="pengelolaan-kinerja"
        data-testid="pengelolaan-kinerja-layout"
        className={clsx([
          'text-default bg-gray-10 min-h-screen min-w-[1025px]',
          hasHeader && !isLoading && 'pt-[4.25rem]',
          printablePage && 'print:bg-white print:p-0',
        ])}
      >
        {/* While waiting for the user profile to load, you can opt-out from handling the loading by removing the `disableLoading` props on RoleContentProvider */}
        {isLoading && <RenderSpinner />}

        {/* Header */}
        {hasHeader && (
          <PageHeader
            title={title}
            className={clsx(
              'print:!hidden',
              IS_DEVELOPMENT && 'border-l-4 border-primary border-solid',
              IS_DEVELOPMENT &&
                centered &&
                'border-r-4 border-primary border-solid'
            )}
          >
            {(isGuru || isPamong || isGpkPegawai) && <RenderResetButtons />}
          </PageHeader>
        )}

        {/* Content */}
        {!centered && <>{children}</>}

        {centered && (
          <div
            className={clsx('px-8 pb-8 pt-12 min-h-full', {
              'print:p-0': printablePage,
            })}
          >
            <div
              className={clsx('max-w-7xl mx-auto', {
                'min-w-[1280px]': isDesktopBrowser,
              })}
            >
              {children}
            </div>
          </div>
        )}
      </div>
    </>
  )
}
export default KinerjaLayoutBase
